import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { LineChart, PieChart } from '@mui/x-charts';
import { useNavigate, useParams } from 'react-router-dom';
import { getPoll, getPollAnswers, getPollSends, getPollStats } from '../Api';
import type {
    PollAnswerFilter,
    PollAnswerRowDto,
    PollDto,
    PollSendDto,
    PollStatsDto,
} from '../models/dtos/PollDto';
import {
    formatDateTime,
    languageLabel,
    percent,
    PollSendDialog,
    PollSendsTable,
    PollStatusChip,
} from '../components/polls/PollShared';

const STATUS_LABELS: Record<string, string> = {
    SENT: 'Not opened',
    OPENED: 'Opened',
    DISMISSED: 'Closed',
    ANSWERED: 'Answered',
};

function Kpi({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
    return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
            <CardContent>
                <Typography variant="overline">{label}</Typography>
                <Typography variant="h4">{value}</Typography>
                {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
            </CardContent>
        </Card>
    );
}

export default function PollStatsPage() {
    const id = useParams()['id'] as string;
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [poll, setPoll] = React.useState<PollDto | null>(null);
    const [stats, setStats] = React.useState<PollStatsDto | null>(null);
    const [sends, setSends] = React.useState<PollSendDto[]>([]);
    const [sendId, setSendId] = React.useState<string>('');
    const [sendOpen, setSendOpen] = React.useState(false);

    const [filter, setFilter] = React.useState<PollAnswerFilter>('RESPONSES');
    const [optionKey, setOptionKey] = React.useState<string | null>(null);
    const [includeTest, setIncludeTest] = React.useState(false);
    const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 50 });
    const [answers, setAnswers] = React.useState<PollAnswerRowDto[]>([]);
    const [answersTotal, setAnswersTotal] = React.useState(0);
    const [answersLoading, setAnswersLoading] = React.useState(false);

    const loadOverview = React.useCallback(async () => {
        const [p, s, history] = await Promise.all([getPoll(id), getPollStats(id, sendId || null), getPollSends(id, 0, 100)]);
        setPoll(p);
        setStats(s);
        setSends(history?.items ?? []);
    }, [id, sendId]);

    React.useEffect(() => {
        // Only the first load shows the spinner; switching the send filter refreshes in place.
        (async () => {
            await loadOverview();
            setLoading(false);
        })();
    }, [loadOverview]);

    const running = sends.some(s => s.status === 'PENDING' || s.status === 'SENDING');
    React.useEffect(() => {
        if (!running) return;
        const timer = window.setInterval(loadOverview, 3000);
        return () => window.clearInterval(timer);
    }, [running, loadOverview]);

    React.useEffect(() => {
        (async () => {
            setAnswersLoading(true);
            const page = await getPollAnswers(id, {
                filter,
                sendId: sendId || null,
                optionKey,
                includeTest,
                offset: pagination.page * pagination.pageSize,
                limit: pagination.pageSize,
            });
            setAnswers(page?.items ?? []);
            setAnswersTotal(page?.totalItems ?? 0);
            setAnswersLoading(false);
        })();
    }, [id, filter, sendId, optionKey, includeTest, pagination]);

    const showAnswers = (next: PollAnswerFilter, key: string | null = null) => {
        setFilter(next);
        setOptionKey(key);
        setPagination(p => ({ ...p, page: 0 }));
    };

    const columns: GridColDef<PollAnswerRowDto>[] = [
        {
            field: 'user', headerName: 'User', width: 200, sortable: false,
            valueGetter: params => params.row.userName ?? '',
            renderCell: params => (
                <Stack>
                    <Typography variant="body2">{params.row.userName ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        #{params.row.userRef}{params.row.test ? ' · test' : ''}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: 'status', headerName: 'Status', width: 120, sortable: false,
            renderCell: params => <Chip size="small" label={STATUS_LABELS[params.row.status] ?? params.row.status}
                color={params.row.status === 'ANSWERED' ? 'success' : params.row.status === 'DISMISSED' ? 'warning' : 'default'} />,
        },
        {
            field: 'answer', headerName: 'Answer', flex: 1, minWidth: 260, sortable: false,
            valueGetter: params => params.row.customText ?? params.row.optionText ?? '',
            renderCell: params => params.row.customText ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: 'italic' }}>
                    “{params.row.customText}”
                </Typography>
            ) : (
                <Typography variant="body2">{params.row.optionText ?? '—'}</Typography>
            ),
        },
        { field: 'lang', headerName: 'Lang', width: 70, sortable: false, valueGetter: params => params.row.lang?.toUpperCase() ?? '' },
        {
            field: 'client', headerName: 'App', width: 110, sortable: false,
            valueGetter: params => [params.row.osType, params.row.buildNumber].filter(v => v != null).join(' ') || '—',
        },
        { field: 'dismissCount', headerName: 'Closed ×', width: 80, sortable: false, type: 'number' },
        {
            field: 'when', headerName: 'When', width: 180, sortable: false,
            valueGetter: params => formatDateTime(params.row.answeredAt ?? params.row.dismissedAt ?? params.row.openedAt ?? params.row.sentAt),
        },
    ];

    if (loading) {
        return <Box sx={{ mx: 3, my: 3 }}><CircularProgress /></Box>;
    }

    const s = stats;
    const pending = s?.pending ?? 0;
    const optionTotal = (s?.options.reduce((sum, o) => sum + o.count, 0) ?? 0) + (s?.customAnswers ?? 0);
    const selectedSend = sends.find(x => x.id === sendId);

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Typography variant="h4">{poll?.name ?? 'Poll'}</Typography>
                            {poll && <PollStatusChip status={poll.status} />}
                        </Stack>
                        <Typography variant="body1" color="text.secondary">
                            {poll?.texts[poll.defaultLanguage]?.question}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={1}>
                        <Button onClick={() => navigate('/marketing/polls')}>Back</Button>
                        <Button variant="outlined" onClick={() => navigate('/marketing/polls/' + encodeURIComponent(id) + '/edit')}>Edit</Button>
                        {poll?.status !== 'CLOSED' && <Button variant="contained" onClick={() => setSendOpen(true)}>Send</Button>}
                    </Stack>
                </Stack>

                <TextField select size="small" label="Send" value={sendId} sx={{ maxWidth: 420 }}
                    SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                    onChange={e => { setSendId(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
                    helperText={selectedSend ? 'Users whose latest push came from this send' : 'Every targeted user (test sends excluded)'}>
                    <MenuItem value="">All sends</MenuItem>
                    {sends.filter(x => !x.test).map(x => (
                        <MenuItem key={x.id} value={x.id}>
                            {formatDateTime(x.createdAt)} · {x.userTags.join(', ')} · {x.targetedCount.toLocaleString()} users
                        </MenuItem>
                    ))}
                </TextField>

                <Stack direction="row" gap={2} flexWrap="wrap">
                    <Kpi label="Targeted" value={(s?.targeted ?? 0).toLocaleString()} />
                    <Kpi label="Opened" value={(s?.opened ?? 0).toLocaleString()} hint={percent(s?.opened ?? 0, s?.targeted ?? 0)} />
                    <Kpi label="Answered" value={(s?.answered ?? 0).toLocaleString()} hint={`${percent(s?.answered ?? 0, s?.targeted ?? 0)} of targeted`} />
                    <Kpi label="Closed without answer" value={(s?.dismissed ?? 0).toLocaleString()} hint={percent(s?.dismissed ?? 0, s?.targeted ?? 0)} />
                    <Kpi label="No reaction yet" value={pending.toLocaleString()} />
                    <Kpi label="Answer rate" value={percent(s?.answered ?? 0, (s?.answered ?? 0) + (s?.dismissed ?? 0))}
                        hint="answered ÷ (answered + closed)" />
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' }, gap: 2 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Answers</Typography>
                            <Stack gap={1.5}>
                                {[...(s?.options ?? []).map(o => ({ key: o.key, label: o.text, removed: !o.exists, count: o.count })),
                                    ...(poll?.allowCustomAnswer || (s?.customAnswers ?? 0) > 0
                                        ? [{ key: '__custom', label: 'Custom answer', removed: false, count: s?.customAnswers ?? 0 }]
                                        : [])]
                                    .map(row => (
                                        <Box key={row.key} sx={{ cursor: 'pointer' }}
                                            onClick={() => (row.key === '__custom' ? showAnswers('CUSTOM') : showAnswers('OPTION', row.key))}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="body2" sx={{ fontStyle: row.key === '__custom' ? 'italic' : undefined }}>
                                                    {row.label}{row.removed ? ' (removed)' : ''}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.count.toLocaleString()} · {percent(row.count, optionTotal)}
                                                </Typography>
                                            </Stack>
                                            <LinearProgress variant="determinate" sx={{ height: 8, borderRadius: 4 }}
                                                value={optionTotal > 0 ? (row.count / optionTotal) * 100 : 0} />
                                        </Box>
                                    ))}
                            </Stack>
                        </CardContent>
                    </Card>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Reactions</Typography>
                            {(s?.targeted ?? 0) > 0 ? (
                                <PieChart
                                    height={220}
                                    series={[{
                                        innerRadius: 50,
                                        data: [
                                            { id: 0, value: s?.answered ?? 0, label: 'Answered', color: '#2e7d32' },
                                            { id: 1, value: s?.dismissed ?? 0, label: 'Closed', color: '#ed6c02' },
                                            { id: 2, value: Math.max(0, (s?.opened ?? 0) - (s?.answered ?? 0) - (s?.dismissed ?? 0)), label: 'Opened only', color: '#0288d1' },
                                            { id: 3, value: Math.max(0, (s?.targeted ?? 0) - (s?.opened ?? 0)), label: 'Not opened', color: '#9e9e9e' },
                                        ],
                                    }]}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">Nobody targeted yet.</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' }, gap: 2 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Responses per day (UTC)</Typography>
                            {(s?.daily.length ?? 0) > 0 ? (
                                <LineChart
                                    height={260}
                                    xAxis={[{ scaleType: 'point', data: s!.daily.map(d => d.day) }]}
                                    series={[
                                        { data: s!.daily.map(d => d.answered), label: 'Answered', color: '#2e7d32' },
                                        { data: s!.daily.map(d => d.dismissed), label: 'Closed', color: '#ed6c02' },
                                    ]}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">No responses yet.</Typography>
                            )}
                        </CardContent>
                    </Card>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>By language served</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Language</TableCell>
                                        <TableCell align="right">Targeted</TableCell>
                                        <TableCell align="right">Answered</TableCell>
                                        <TableCell align="right">Closed</TableCell>
                                        <TableCell align="right">Response</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(s?.languages ?? []).map(l => (
                                        <TableRow key={l.lang}>
                                            <TableCell>{languageLabel(l.lang)}</TableCell>
                                            <TableCell align="right">{l.targeted.toLocaleString()}</TableCell>
                                            <TableCell align="right">{l.answered.toLocaleString()}</TableCell>
                                            <TableCell align="right">{l.dismissed.toLocaleString()}</TableCell>
                                            <TableCell align="right">{percent(l.answered, l.targeted)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Box>

                <Card variant="outlined">
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
                            <Tabs value={filter === 'OPTION' ? 'OPTION' : filter} onChange={(_, value) => showAnswers(value)} variant="scrollable">
                                <Tab value="RESPONSES" label="All responses" />
                                <Tab value="ANSWERED" label="Answered" />
                                <Tab value="CUSTOM" label={`Custom (${(s?.customAnswers ?? 0).toLocaleString()})`} />
                                <Tab value="DISMISSED" label="Closed" />
                                <Tab value="PENDING" label="No reaction" />
                                {filter === 'OPTION' && <Tab value="OPTION" label="Option" />}
                            </Tabs>
                            <FormControlLabel control={<Switch checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />}
                                label="Include test sends" />
                        </Stack>
                        {filter === 'OPTION' && optionKey && (
                            <Chip sx={{ my: 1 }} label={`Option: ${s?.options.find(o => o.key === optionKey)?.text ?? optionKey}`}
                                onDelete={() => showAnswers('ANSWERED')} />
                        )}
                        <Box sx={{ mt: 1 }}>
                            <DataGrid
                                autoHeight
                                rows={answers}
                                columns={columns}
                                getRowId={row => row.id}
                                getRowHeight={() => 'auto'}
                                loading={answersLoading}
                                paginationMode="server"
                                rowCount={answersTotal}
                                paginationModel={pagination}
                                onPaginationModelChange={setPagination}
                                pageSizeOptions={[25, 50, 100]}
                                disableRowSelectionOnClick
                                sx={{ '& .MuiDataGrid-cell': { py: 1, alignItems: 'flex-start' } }}
                                localeText={{ noRowsLabel: 'No users here' }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>Send history</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Click a send to filter the stats by it.</Typography>
                        <PollSendsTable sends={sends} selectedSendId={sendId}
                            onSelect={send => { if (!send.test) setSendId(send.id === sendId ? '' : send.id); }} />
                    </CardContent>
                </Card>
            </Stack>
            <PollSendDialog
                poll={poll}
                open={sendOpen}
                onClose={() => setSendOpen(false)}
                onSent={() => { setSendOpen(false); loadOverview(); }}
            />
        </Box>
    );
}
